import { Button, Form, InputNumber, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { FormInstance, FormListFieldData } from 'antd';
import type { Rule } from 'antd/es/form';
import type { CloudCmd, EventMode, TriggerType } from '@/apis/simulation';

const ACCENT = '#5e6ad2';

const TRIGGER_TYPE_OPTIONS: { label: string; value: TriggerType }[] = [
  { label: '定时触发 (time)', value: 'time' },
  { label: '区域触发 (position)', value: 'position' },
];

const MODE_OPTIONS: { label: string; value: EventMode }[] = [
  { label: '触发一次 (once)', value: 'once' },
  { label: '区域内持续 (while_inside)', value: 'while_inside' },
];

const CLOUD_CMD_OPTIONS: { label: string; value: CloudCmd }[] = [
  { label: '通过红绿灯 (TRAFFIC_LIGHT_PASS)', value: 'TRAFFIC_LIGHT_PASS' },
  { label: '左绕行 (DETOUR_LEFT)', value: 'DETOUR_LEFT' },
  { label: '右绕行 (DETOUR_RIGHT)', value: 'DETOUR_RIGHT' },
  { label: '左变道 (LANE_CHANGE_LEFT)', value: 'LANE_CHANGE_LEFT' },
  { label: '右变道 (LANE_CHANGE_RIGHT)', value: 'LANE_CHANGE_RIGHT' },
  { label: '回归路线 (BACK_TO_ROUTE)', value: 'BACK_TO_ROUTE' },
  { label: '前进 (MOVE_FORWARD)', value: 'MOVE_FORWARD' },
  { label: '后退 (MOVE_BACKWARD)', value: 'MOVE_BACKWARD' },
];

const ACTION_TYPE_OPTIONS = [{ label: '云控 (cloud_control)', value: 'cloud_control' }];

/**
 * 校验「必填且 > 0」的数值（半径 / 持续时间）。
 * InputNumber 清空时值为 null/undefined，需同时覆盖缺失与 <= 0 两种情况。
 */
const positiveNumberRule = (label: string): Rule[] => [
  {
    validator: async (_rule, value: number | undefined | null) => {
      if (value == null) {
        throw new Error(`请输入${label}`);
      }
      if (value <= 0) {
        throw new Error(`${label}必须大于 0`);
      }
    },
  },
];

interface EventItemEditorProps {
  field: FormListFieldData;
  form: FormInstance;
  onRemove: () => void;
}

/**
 * 单条云端控制事件编辑器。挂在 antd <Form.List name="events"> 的每行上，
 * 按 trigger.type / mode 条件渲染字段并施加行级校验（plan §3.2/§3.4）。
 *
 * Form.useWatch 读取的是 form store 的完整路径（不受 Form.List 上下文自动补前缀），
 * 故路径为 ['events', field.name, ...]；Form.Item 的 name 仍用相对 [field.name, ...]
 * 由 ListContext 自动补 'events' 前缀。
 */
export function EventItemEditor({ field, form, onRemove }: EventItemEditorProps) {
  const triggerType = Form.useWatch(['events', field.name, 'trigger', 'type'], form);
  const mode = Form.useWatch(['events', field.name, 'mode'], form);
  const count = Form.useWatch(['events', field.name, 'count'], form);
  const isPosition = triggerType === 'position';
  const isOnce = mode === 'once';
  const isWhileInside = mode === 'while_inside';
  // time 触发固定 once（无"区域内持续"语义）；mode 选择仅 position 出现
  const showModeSelect = isPosition;
  // frequency 显示：once（多次间隔）或 while_inside（持续发送）；once 且 count=1 时可选
  const showFrequency = isOnce || isWhileInside;
  const frequencyRequired = isWhileInside || (isOnce && Number(count) > 1);

  return (
    <div
      style={{
        border: `1px solid ${ACCENT}22`,
        borderRadius: 8,
        padding: '8px 12px 0',
        marginBottom: 8,
        background: '#fafaff',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <span style={{ fontWeight: 600, color: ACCENT, fontSize: 13 }}>云端控制事件</span>
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={onRemove} />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Form.Item
          label="触发类型"
          name={[field.name, 'trigger', 'type']}
          rules={[{ required: true, message: '请选择触发类型' }]}
          style={{ flex: '1 1 200px' }}
        >
          <Select options={TRIGGER_TYPE_OPTIONS} placeholder="time / position" />
        </Form.Item>
        {isPosition ? (
          <>
            <Form.Item
              label="X 坐标 (UTM)"
              name={[field.name, 'trigger', 'position', 'x']}
              rules={[{ required: true, message: '请输入 x' }]}
              style={{ flex: '1 1 120px' }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="如 1.0" />
            </Form.Item>
            <Form.Item
              label="Y 坐标 (UTM)"
              name={[field.name, 'trigger', 'position', 'y']}
              rules={[{ required: true, message: '请输入 y' }]}
              style={{ flex: '1 1 120px' }}
            >
              <InputNumber style={{ width: '100%' }} placeholder="如 2.0" />
            </Form.Item>
            <Form.Item
              label="半径 (米)"
              name={[field.name, 'trigger', 'radius']}
              rules={positiveNumberRule('半径')}
              style={{ flex: '1 1 120px' }}
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="如 3.0" />
            </Form.Item>
          </>
        ) : (
          <Form.Item
            label="触发时间 (秒)"
            name={[field.name, 'trigger', 'time']}
            rules={[
              { required: true, message: '请输入触发时间' },
              { type: 'number', min: 0, message: '时间必须 >= 0' },
            ]}
            style={{ flex: '1 1 120px' }}
          >
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="如 10" />
          </Form.Item>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {showModeSelect ? (
          <Form.Item
            label="模式"
            name={[field.name, 'mode']}
            rules={[{ required: true, message: '请选择模式' }]}
            style={{ flex: '1 1 200px' }}
          >
            <Select options={MODE_OPTIONS} placeholder="once / while_inside" />
          </Form.Item>
        ) : null}
        {isOnce ? (
          <Form.Item
            label="触发次数"
            name={[field.name, 'count']}
            rules={[{ type: 'number', min: 1, message: '次数必须 >= 1' }]}
            style={{ flex: '1 1 120px' }}
          >
            <InputNumber min={1} step={1} precision={0} style={{ width: '100%' }} placeholder="1" />
          </Form.Item>
        ) : null}
        {showFrequency ? (
          <Form.Item
            label={isWhileInside ? '持续发送频率 (秒)' : '多次触发间隔 (秒)'}
            name={[field.name, 'frequency']}
            extra={isOnce && !frequencyRequired ? 'count=1 时可不填（只触发一次）' : undefined}
            rules={[
              {
                validator: async (_rule, value: number | undefined | null) => {
                  if (frequencyRequired && value == null) {
                    throw new Error('请输入频率');
                  }
                  if (value != null && value <= 0) {
                    throw new Error('频率必须大于 0');
                  }
                },
              },
            ]}
            style={{ flex: '1 1 140px' }}
          >
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="如 2" />
          </Form.Item>
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Form.Item
          label="动作类型"
          name={[field.name, 'action', 'type']}
          rules={[{ required: true, message: '请选择动作类型' }]}
          style={{ flex: '1 1 200px' }}
        >
          {/* 一阶段固定 cloud_control，禁用切换，预留未来扩展（plan A-3 / FQ-4） */}
          <Select options={ACTION_TYPE_OPTIONS} disabled />
        </Form.Item>
        <Form.Item
          label="云控指令"
          name={[field.name, 'action', 'cloudCmd']}
          rules={[{ required: true, message: '请选择云控指令' }]}
          style={{ flex: '1 1 200px' }}
        >
          <Select options={CLOUD_CMD_OPTIONS} placeholder="选择云控指令" />
        </Form.Item>
      </div>
    </div>
  );
}

export default EventItemEditor;
